import { useState, useMemo, useEffect } from 'react';
import { useManagePin } from '@/features/card/hooks/useCard/useManagePin';
import { validateRepeated, validateConsecutive } from './changeNip.validators';
import { NIP_STEPS, PIN_LENGTH } from './changeNip.constants';
import { useToast } from '@chikara/ui';
import { t } from '@/i18n';

interface UseChangeNipFormProps {
    isOpen: boolean;
    onClose: () => void;
    cardUuid: string;
    accountUuid: string;
}

export const useChangeNipForm = ({ isOpen, onClose, cardUuid, accountUuid }: UseChangeNipFormProps) => {
    const toast = useToast();
    const { changePin, isLoading } = useManagePin();

    const [step, setStep] = useState<number>(NIP_STEPS.INITIAL);
    const [pin, setPin] = useState('');
    const [pinConfirmation, setPinConfirmation] = useState('');
    const [localError, setLocalError] = useState<string | null>(null);

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            setStep(NIP_STEPS.INITIAL);
            setPin('');
            setPinConfirmation('');
            setLocalError(null);
        }
    }, [isOpen]);

    const isPinValid = useMemo(() => {
        if (pin.length < PIN_LENGTH) return false;
        if (validateRepeated(pin)) return false;
        if (validateConsecutive(pin)) return false;
        return true;
    }, [pin]);

    const isConfirmationValid = useMemo(() => {
        return pinConfirmation.length === PIN_LENGTH && pin === pinConfirmation;
    }, [pin, pinConfirmation]);

    const handlePinChange = (value: string) => {
        setPin(value);
        if (value.length === PIN_LENGTH) {
            if (validateRepeated(value)) {
                setLocalError(t('dashboard.card.detail.modals.changeNip.errors.repeated'));
            } else if (validateConsecutive(value)) {
                setLocalError(t('dashboard.card.detail.modals.changeNip.errors.consecutive'));
            } else {
                setLocalError(null);
            }
        } else {
            setLocalError(null);
        }
    };

    const handlePinConfirmationChange = (value: string) => {
        setPinConfirmation(value);
        if (value.length === PIN_LENGTH && value !== pin) {
            setLocalError(t('dashboard.card.detail.modals.changeNip.errors.mismatch'));
        } else {
            setLocalError(null);
        }
    };

    const handleNext = async () => {
        if (step === NIP_STEPS.INITIAL) {
            setStep(NIP_STEPS.CREATE);
        } else if (step === NIP_STEPS.CREATE) {
            if (!isPinValid) {
                if (validateRepeated(pin)) setLocalError(t('dashboard.card.detail.modals.changeNip.errors.repeated'));
                else if (validateConsecutive(pin)) setLocalError(t('dashboard.card.detail.modals.changeNip.errors.consecutive'));
                return;
            }
            setLocalError(null);
            setStep(NIP_STEPS.CONFIRM);
        } else if (step === NIP_STEPS.CONFIRM) {
            if (pin !== pinConfirmation) {
                setLocalError(t('dashboard.card.detail.modals.changeNip.errors.mismatch'));
                return;
            }
            const result = await changePin({
                account: accountUuid,
                card_uuid: cardUuid,
                pin: btoa(pin)
            });
            if (result.success) {
                setStep(NIP_STEPS.SUCCESS);
            }
        } else if (step === NIP_STEPS.SUCCESS) {
            toast.success(t('dashboard.card.detail.modals.changeNip.success'));
            onClose();
        }
    };

    const isActionDisabled = useMemo(() => {
        if (isLoading) return true;
        if (step === NIP_STEPS.CREATE) return !isPinValid;
        if (step === NIP_STEPS.CONFIRM) return !isConfirmationValid;
        return false;
    }, [isLoading, step, isPinValid, isConfirmationValid]);

    return {
        step,
        localError,
        isLoading,
        isActionDisabled,
        handleNext,
        handlePinChange,
        handlePinConfirmationChange,
    };
};
